import React, { useState } from "react";
import { NavLink, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Menu, Copy, Check } from "lucide-react";
import { toast } from "sonner";

const NAV = [
  { to: "/", label: "Base Camp", testId: "nav-home" },
  { to: "/how", label: "Flywheel", testId: "nav-how" },
  { to: "/donate", label: "Cremate Dust", testId: "nav-donate" },
  { to: "/dashboard", label: "Donor", testId: "nav-donor" },
  { to: "/holder", label: "Holder", testId: "nav-holder" },
  { to: "/treasury", label: "Treasury", testId: "nav-treasury" },
  { to: "/wall", label: "Survivor Wall", testId: "nav-wall" },
];

const TREASURY = "0x9cb8915C36050e787615e13eA9787B3654BE9A15";

export default function Header() {
  const [copied, setCopied] = useState(false);
  const [open, setOpen] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(TREASURY);
      setCopied(true);
      toast.success("Treasury address copied. Send any chain's dust here.", {
        description: TREASURY,
      });
      setTimeout(() => setCopied(false), 1800);
    } catch { toast.error("Copy failed — please copy manually."); }
  };

  const linkCls = ({ isActive }) =>
    `px-3 py-2 text-sm transition-colors ${
      isActive ? "text-[#FFD54F]" : "text-stone-300 hover:text-[#FFC940]"
    }`;

  return (
    <header className="sticky top-0 z-40 border-b border-[#3A2E1C] bg-[#0A0906]/90 backdrop-blur-md">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 flex items-center h-16 gap-4">
        <Link to="/" className="flex items-center gap-2 group" data-testid="brand-link">
          <img
            src="/quodust-logo.png"
            alt="Quodust"
            className="w-10 h-10 rounded-md object-cover ring-1 ring-[#FFAB00]/40 shadow-[0_0_20px_rgba(255,171,0,0.25)]"
          />
          <div className="leading-none">
            <div className="font-display text-2xl tracking-tight">QUODUST<span className="ember-text">.</span></div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-stone-500">quotron terminal</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center ml-4">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} className={linkCls} data-testid={n.testId}>
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="ml-auto flex items-center gap-2">
          <button
            data-testid="copy-treasury-btn"
            onClick={copy}
            className="hidden sm:flex items-center gap-2 h-10 px-3 rounded-md border border-[#FFAB00]/40 bg-[#141008] hover:bg-[#1E1810] transition-colors"
          >
            {copied ? <Check className="w-4 h-4 text-[#10B981]" /> : <Copy className="w-4 h-4 text-[#FFC940]" />}
            <span className="mono text-xs text-stone-300">0x9cb8…9A15</span>
          </button>

          <Link to="/donate">
            <Button
              data-testid="header-donate-cta"
              className="h-10 bg-[#FFAB00] hover:bg-[#FFC940] text-[#0A0906] font-semibold"
            >
              Cremate Dust
            </Button>
          </Link>

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                className="lg:hidden h-10 w-10 inline-flex items-center justify-center rounded-md border border-[#3A2E1C]"
                data-testid="mobile-menu-trigger"
                aria-label="menu"
              >
                <Menu className="w-5 h-5 text-stone-200" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-[#0A0906] border-[#3A2E1C] text-stone-100 w-72">
              <div className="mt-6 flex flex-col gap-1">
                {NAV.map((n) => (
                  <NavLink
                    key={n.to}
                    to={n.to}
                    onClick={() => setOpen(false)}
                    className={({ isActive }) =>
                      `px-3 py-3 rounded-md ${isActive ? "bg-[#1E1810] text-[#FFD54F]" : "text-stone-200 hover:bg-[#141008]"}`
                    }
                    data-testid={`${n.testId}-mobile`}
                  >
                    {n.label}
                  </NavLink>
                ))}
                <NavLink
                  to="/admin"
                  onClick={() => setOpen(false)}
                  className="mt-4 px-3 py-3 rounded-md text-stone-400 border border-[#3A2E1C]"
                  data-testid="nav-admin-mobile"
                >
                  Admin console →
                </NavLink>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
